import React, { useEffect, useRef, useState } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import Navbar from '@/components/ui/navbar';
import Typography from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToastHelpers } from '@/utils/toast';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  cancelExtraction,
  getJobProgress,
  listExtractionRequests,
  startExtractAsync,
  submitExtractionRecords,
  DataExtractionResponse,
  DataExtractionRequestModel,
  DataExtractionRow,
  JobProgressData,
} from '@/services/dataExtractionService';

const POLL_MS = 1500;

const DataExtraction: React.FC = () => {
  const { showError, showSuccess } = useToastHelpers();
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [result, setResult] = useState<DataExtractionResponse | null>(null);
  const [jobId, setJobId] = useState('');
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgressData | null>(null);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [activeEhr, setActiveEhr] = useState<string | null>(null);
  const [activeRequestUuid, setActiveRequestUuid] = useState<string | null>(
    null,
  );
  const [showPreviousExtractions, setShowPreviousExtractions] = useState(false);
  const [previousExtractionsLoading, setPreviousExtractionsLoading] =
    useState(false);
  const [previousExtractionsError, setPreviousExtractionsError] = useState('');
  const [previousExtractions, setPreviousExtractions] = useState<
    DataExtractionRequestModel[]
  >([]);
  const [csvOutput, setCsvOutput] = useState('');
  const [extractError, setExtractError] = useState('');
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPollAtRef = useRef<number>(0);
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const selectedHospital = useSelector(
    (state: RootState) => state.hospitals.selectedHospital,
  );
  const [practiceIdInput, setPracticeIdInput] = useState('');
  const [fetchWithClaim, setFetchWithClaim] = useState(false);
  useEffect(() => {
    setPracticeIdInput(selectedHospital?.hospitalCode ?? '');
  }, [selectedHospital?.hospitalCode]);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
  }, [showError, showSuccess]);

  const practiceId = practiceIdInput.trim();

  const decodeHtmlEntities = (text: string): string => {
    let cleaned = text;

    // Normalize all non-breaking space variants (with/without semicolon, with amp; prefix).
    cleaned = cleaned.replace(/(&(amp;)?nbsp;?)/gi, ' ');

    cleaned = cleaned
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&apos;/gi, "'");

    // Collapse multiple spaces to a single space.
    cleaned = cleaned.replace(/[ \t]+/g, ' ');

    return cleaned;
  };

  const stripHtml = (html: string | undefined | null): string => {
    if (!html) return '';

    // Normalize common block tags to line breaks, then remove all tags.
    let text = html
      .replace(/<(br|BR)\s*\/?>/g, '\n')
      .replace(/<\/(p|div|li|tr|td|h[1-6])>/gi, '\n')
      .replace(/<(p|div|li|tr|td|h[1-6])[^>]*>/gi, '');

    text = text.replace(/<[^>]+>/g, '');
    text = decodeHtmlEntities(text);

    // Collapse excessive whitespace/newlines.
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .join('\n');
  };

  const extractHpiNotes = (item: DataExtractionRow): string => {
    return (
      item.hpiNotes?.hpiNotes
        ?.map((n) => stripHtml(n.content))
        .filter(Boolean)
        .join('\n\n') || ''
    );
  };

  const extractPhysicalExamNotes = (item: DataExtractionRow): string => {
    return stripHtml(item.physicalExam?.summarytext || '');
  };

  const extractAssessmentNotes = (item: DataExtractionRow): string => {
    return stripHtml(item.assessment?.assessmenttext || '');
  };

  const extractProcedureNotes = (item: DataExtractionRow): string => {
    return (
      item.procedureDocumentation?.proceduredocumentation
        ?.map((p) => stripHtml(p.proceduredocumentationtext))
        .filter(Boolean)
        .join('\n\n') || ''
    );
  };

  const extractNotes = (item: DataExtractionRow): string => {
    const sections = [
      ['HPI', extractHpiNotes(item)],
      ['Physical Exam', extractPhysicalExamNotes(item)],
      ['Assessment', extractAssessmentNotes(item)],
      ['Procedure', extractProcedureNotes(item)],
    ] as const;

    return sections
      .filter(([, content]) => Boolean(content))
      .map(([label, content]) => `${label}:\n${content}`)
      .join('\n\n-----\n\n');
  };

  const extractAppointmentType = (item: DataExtractionRow): string => {
    if (!item.appointmentData?.length) {
      return '';
    }

    const appointmentTypes = item.appointmentData
      .map((entry) => entry.appointmenttype?.trim())
      .filter((value): value is string => Boolean(value));

    return Array.from(new Set(appointmentTypes)).join(' | ');
  };

  const extractAppointmentTime = (item: DataExtractionRow): string => {
    if (!item.appointmentData?.length) {
      return '';
    }

    const appointmentTimes = item.appointmentData
      .map((entry) => {
        const date = (entry.date || '').trim();
        const time = (entry.starttime || '').trim();
        if (date && time) return `${date} ${time}`;
        return date || time;
      })
      .filter((value): value is string => Boolean(value));

    return Array.from(new Set(appointmentTimes)).join(' | ');
  };

  const extractEncounterProceduresJson = (item: DataExtractionRow): string => {
    const findByEncounterServicesKey = (value: unknown): unknown => {
      if (value === null || value === undefined) return undefined;
      if (Array.isArray(value)) {
        for (const v of value) {
          const found = findByEncounterServicesKey(v);
          if (found !== undefined) return found;
        }
        return undefined;
      }

      if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        for (const [k, v] of Object.entries(obj)) {
          const keyLower = k.toLowerCase();
          const normalized = keyLower.replace(/_/g, '');

          if (
            normalized === 'encounterservices' ||
            (normalized.includes('encounter') && normalized.includes('service'))
          ) {
            return v;
          }

          const found = findByEncounterServicesKey(v);
          if (found !== undefined) return found;
        }
      }

      return undefined;
    };

    const encounterServicesPayload = findByEncounterServicesKey(item);
    if (
      encounterServicesPayload === null ||
      encounterServicesPayload === undefined ||
      typeof encounterServicesPayload !== 'object'
    ) {
      return '';
    }

    const payloadObj = encounterServicesPayload as Record<string, unknown>;
    const services = payloadObj.services as Record<string, unknown> | undefined;
    const procedures = services?.procedures;

    if (!Array.isArray(procedures)) {
      return '';
    }

    try {
      return JSON.stringify(procedures);
    } catch {
      return String(procedures);
    }
  };

  const extractEncounterProcedureCodes = (item: DataExtractionRow): string => {
    const encounterProceduresJson = extractEncounterProceduresJson(item);
    if (!encounterProceduresJson) return '';

    try {
      const procedures = JSON.parse(encounterProceduresJson) as unknown[];
      const codes = procedures
        .map((p) => {
          if (p === null || typeof p !== 'object') return '';
          const obj = p as Record<string, unknown>;
          const code = (obj.procedurecode ?? obj.procedureCode ?? '') as
            | string
            | number;
          return String(code).trim();
        })
        .filter(Boolean);

      return Array.from(new Set(codes)).join(' | ');
    } catch {
      return '';
    }
  };

  const previewRows = (result?.data?.slice(0, 10) || []).map((row) => ({
    ...row,
    _notes: extractNotes(row),
  }));

  const hasClaimIdInResults = (result?.data || []).some(
    (row) => row.csvData?.claimid,
  );

  // Parse CSV string into rows for visualization when result.data is absent (e.g. ECW flat CSV)
  const parsedCsvRows = React.useMemo<Record<string, string>[]>(() => {
    const raw = (csvOutput || result?.csv || '').trim();
    if (!raw || result?.data?.length) return [];

    const lines = raw.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];

    const parseRow = (line: string): string[] => {
      const values: string[] = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          values.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      values.push(cur);
      return values;
    };

    const headers = parseRow(lines[0]);
    return lines.slice(1).map((line) => {
      const vals = parseRow(line);
      const row: Record<string, string> = {};
      headers.forEach((h, i) => {
        row[h] = vals[i] ?? '';
      });
      return row;
    });
  }, [csvOutput, result]);

  const parsedCsvHeaders = React.useMemo<string[]>(() => {
    const raw = (csvOutput || result?.csv || '').trim();
    if (!raw || result?.data?.length) return [];
    const firstLine = raw.split(/\r?\n/)[0];
    if (!firstLine) return [];
    return firstLine.split(',').map((h) => h.replace(/^"|"$/g, '').trim());
  }, [csvOutput, result]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (!selectedFile) {
      setFile(null);
      return;
    }

    if (
      selectedFile.type !== 'text/csv' &&
      !selectedFile.name.toLowerCase().endsWith('.csv')
    ) {
      showError('Please upload a valid CSV file.');
      event.target.value = '';
      return;
    }

    setFile(selectedFile);
  };

  const formatJobErrors = (errors: unknown[] | undefined): string => {
    if (!errors?.length) return '';
    return errors
      .map((e) => (typeof e === 'string' ? e : JSON.stringify(e)))
      .join('; ');
  };

  useEffect(() => {
    if (!pollingJobId) return;

    let cancelled = false;
    let inFlight = false;

    const stopPolling = () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };

    const handleTerminal = async (job: JobProgressData) => {
      stopPolling();
      if (cancelled) return;
      setPollingJobId(null);
      setLoading(false);

      const de = job.result?.dataExtractor;
      if (de) {
        setResult(de);
        setCsvOutput(de?.csv || '');
      }

      const st = String(jobProgress?.status || '').toLowerCase();
      if (st === 'completed') {
        let recordsSaveFailed = false;
        const requestId = activeRequestUuid || pollingJobId;
        if (de && activeTenantId && requestId) {
          try {
            await submitExtractionRecords(
              activeTenantId,
              requestId,
              de,
              activeEhr || undefined,
            );
          } catch (error) {
            recordsSaveFailed = true;
            const message =
              error instanceof Error
                ? error.message
                : 'Failed to save extraction records.';
            setExtractError(message);
            showErrorRef.current(message);
          }
        }
        setActiveTenantId(null);
        setActiveEhr(null);
        setActiveRequestUuid(null);
        if (!recordsSaveFailed) {
          showSuccessRef.current('Extraction completed.');
        }
        return;
      }
      if (st === 'failed') {
        const msg =
          formatJobErrors(job.errors) ||
          job.message ||
          'Extraction job failed.';
        setExtractError(msg);
        showErrorRef.current(msg);
        setActiveTenantId(null);
        setActiveEhr(null);
        setActiveRequestUuid(null);
        return;
      }
      if (st === 'cancelled') {
        setActiveTenantId(null);
        setActiveEhr(null);
        setActiveRequestUuid(null);
        if (de && (de.csv || de.data?.length)) {
          showSuccessRef.current(
            'Job cancelled; partial results are available.',
          );
        } else {
          showSuccessRef.current('Job cancelled.');
        }
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      const elapsed = Date.now() - lastPollAtRef.current;
      const delay = Math.max(POLL_MS - elapsed, 0);
      pollIntervalRef.current = setTimeout(() => void tick(), delay);
    };

    const isTerminal = (job: JobProgressData): boolean => {
      const st = String(job.status || '')
        .toLowerCase()
        .trim();
      if (['completed', 'failed', 'cancelled'].includes(st)) return true;
      // Backend may attach final result while status is stale/missing.
      return Boolean(job.result?.dataExtractor);
    };

    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      lastPollAtRef.current = Date.now();
      try {
        const response = await getJobProgress(
          pollingJobId || '',
          activeTenantId || '',
          activeEhr || undefined,
        );
        if (cancelled) return;
        setJobProgress(response.data);
        if (response.data.errors && response.data.errors.length) {
          setExtractError(
            response.data.errors
              .map((e) => (e as unknown as { error: string }).error)
              .join(', '),
          );
          showErrorRef.current(
            response.data.errors
              .map((e) => (e as unknown as { error: string }).error)
              .join(', '),
          );
          setLoading(false);
          setPollingJobId(null);
          setActiveTenantId(null);
          setActiveRequestUuid(null);
          return;
        } else if (isTerminal(response.data)) {
          await handleTerminal(response.data);
          return;
        } else {
          scheduleNext();
        }
      } catch (error) {
        stopPolling();
        if (cancelled) return;
        setPollingJobId(null);
        setLoading(false);
        const message =
          error instanceof Error ? error.message : 'Failed to poll job status.';
        setExtractError(message);
        showErrorRef.current(message);
      } finally {
        inFlight = false;
      }
    };

    void tick();

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [activeRequestUuid, activeTenantId, pollingJobId]);

  const handleExtract = async () => {
    const tenantId = selectedHospital?.tenantId;
    if (!tenantId) {
      showError('Please select a hospital.');
      return;
    }

    if (!practiceId) {
      showError('Please enter a practice ID.');
      return;
    }

    if (!file) {
      showError('Please upload a CSV file.');
      return;
    }

    const fileText = await file.text();
    const rowCountIncludingHeader = fileText
      .split(/\r?\n/)
      .filter((line) => line.trim().length > 0).length;

    if (rowCountIncludingHeader > 1000) {
      const limitMessage =
        'Row limit exceeded: CSV supports up to 1000 rows including header.';
      setExtractError(limitMessage);
      showError(limitMessage);
      return;
    }

    setResult(null);
    setCsvOutput('');
    setExtractError('');
    setJobProgress(null);
    setJobId('');
    setPollingJobId(null);
    setActiveTenantId(tenantId);
    setActiveEhr(selectedHospital?.ehr ?? null);
    setActiveRequestUuid(null);

    setLoading(true);
    try {
      const started = await startExtractAsync(tenantId, practiceId, file, {
        isClaim: fetchWithClaim,
        provider: selectedHospital?.ehr,
      });
      setJobId(started.jobId);
      setPollingJobId(started.jobId);
      setActiveRequestUuid(started.requestUuid ?? null);
    } catch (error) {
      setLoading(false);
      const message =
        error instanceof Error ? error.message : 'Failed to start extraction.';
      setExtractError(message);
      showError(message);
      setActiveTenantId(null);
      setActiveRequestUuid(null);
    }
  };

  const handleCancelExtract = async () => {
    if (!jobId) {
      const message = 'Cancel unavailable: no active job ID.';
      setExtractError(message);
      showError(message);
      return;
    }

    const st = String(jobProgress?.status || '').toLowerCase();
    if (st && !['pending', 'processing'].includes(st)) {
      showError('Job is no longer running; cancel is not applicable.');
      return;
    }

    setIsCancelling(true);
    try {
      await cancelExtraction(
        jobId,
        activeTenantId || '',
        activeEhr || undefined,
      );
      showSuccess('Cancel request sent. Waiting for job to stop…');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to cancel extraction.';
      setExtractError(`Cancel failed: ${message}`);
      showError(`Cancel failed: ${message}`);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCopyCsv = async () => {
    const text = csvOutput || result?.csv || '';
    if (!text) {
      showError('No CSV to copy.');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showSuccess('CSV copied to clipboard.');
    } catch {
      showError('Could not copy to clipboard.');
    }
  };

  const handleTogglePreviousExtractions = async () => {
    if (showPreviousExtractions) {
      setShowPreviousExtractions(false);
      return;
    }

    const tenantId = selectedHospital?.tenantId;
    if (!tenantId) {
      showError('Please select a hospital.');
      return;
    }

    setShowPreviousExtractions(true);
    setPreviousExtractionsLoading(true);
    setPreviousExtractionsError('');
    try {
      const page = await listExtractionRequests(tenantId, {
        pageNo: 0,
        pageSize: 10,
        provider: selectedHospital?.ehr,
      });
      setPreviousExtractions(page.models);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch previous extractions.';
      setPreviousExtractionsError(message);
      showError(message);
    } finally {
      setPreviousExtractionsLoading(false);
    }
  };

  const escapeCsvValue = (
    value: string | number | null | undefined,
  ): string => {
    const text = value === null || value === undefined ? '' : String(value);
    const escaped = text.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const flattenForCsv = (
    value: unknown,
    prefix: string,
    out: Record<string, string>,
  ) => {
    if (value === null || value === undefined) {
      out[prefix] = '';
      return;
    }

    if (Array.isArray(value)) {
      // Preserve arrays fully (notes, appointmentData, etc.)
      out[prefix] = JSON.stringify(value);
      return;
    }

    if (value instanceof Date) {
      out[prefix] = value.toISOString();
      return;
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        out[prefix] = JSON.stringify(value);
        return;
      }

      for (const key of keys) {
        const nextPrefix = prefix ? `${prefix}.${key}` : key;
        flattenForCsv(obj[key], nextPrefix, out);
      }
      return;
    }

    out[prefix] = String(value);
  };

  const handleDownloadCsv = () => {
    const formatTimestampForFilename = (d: Date): string => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(
        d.getMinutes(),
      )}${pad(d.getSeconds())}`;
    };

    const rawCsv = (csvOutput || result?.csv || '').trim();

    const downloadBlob = (content: string, filename: string) => {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    if (!result?.data?.length) {
      if (rawCsv) {
        downloadBlob(
          csvOutput || result?.csv || '',
          `data_extraction_${practiceId || 'report'}_${formatTimestampForFilename(new Date())}.csv`,
        );
        showSuccess('CSV downloaded.');
        return;
      }
      showError('No CSV output available.');
      return;
    }

    // Build enriched CSV from row `data` when present.
    // We flatten row objects using dot-notation; arrays/objects are preserved as JSON strings.
    const baseReportFields = {
      reportName: result.reportName,
      practiceId: result.practiceId,
      chunkSize: result.chunkSize,
      totalRows: result.totalRows,
      processedRows: result.processedRows,
      failedRows: result.failedRows,
      skippedRows: result.skippedRows,
    } as const;

    const flattenedRows: Record<string, string>[] = result.data.map((item) => {
      const flat: Record<string, string> = {};

      // Include report metadata for every row so the CSV is self-contained.
      flattenForCsv(baseReportFields, 'report', flat);
      // Include the extracted row payload (all fields).
      flattenForCsv(item as unknown, 'row', flat);

      // Add a cleaned, concatenated notes field for convenience.
      flat['row.notes_clean'] = extractNotes(item);
      flat['row.hpi_notes_clean'] = extractHpiNotes(item);
      flat['row.physical_exam_notes_clean'] = extractPhysicalExamNotes(item);
      flat['row.assessment_notes_clean'] = extractAssessmentNotes(item);
      flat['row.procedure_notes_clean'] = extractProcedureNotes(item);
      // Keep only appointment type from appointmentData.
      flat['row.appointmenttype'] = extractAppointmentType(item);
      // Add appointment time from appointment details object.
      flat['row.appointment_time'] = extractAppointmentTime(item);
      // Add full list of procedures under encounter services.
      flat['row.encounter_procedures_json'] =
        extractEncounterProceduresJson(item);
      flat['row.encounter_procedure_codes'] =
        extractEncounterProcedureCodes(item);

      return flat;
    });

    const headerSet = new Set<string>();
    for (const row of flattenedRows) {
      Object.keys(row).forEach((k) => headerSet.add(k));
    }

    // Remove verbose raw note structures from CSV headers to avoid junk JSON.
    // Do not use `startsWith('row.assessment')` — it would drop `row.assessment_notes_clean`.
    const isRawAssessmentField = (key: string) =>
      key === 'row.assessment' || key.startsWith('row.assessment.');

    const isErrorsColumn = (key: string) => {
      const lower = key.toLowerCase();
      return (
        lower === 'report.errors' ||
        lower.endsWith('.errors') ||
        lower.endsWith('.error')
      );
    };

    const headers = Array.from(headerSet)
      .filter(
        (key) =>
          !isErrorsColumn(key) &&
          !key.startsWith('row.appointmentData') &&
          !key.startsWith('row.encounterServices') &&
          !key.startsWith('row.encounterservices') &&
          key !== 'row.encounter_services_json' &&
          !key.startsWith('row.hpiNotes') &&
          !isRawAssessmentField(key) &&
          !key.startsWith('row.physicalExam') &&
          !key.startsWith('row.procedureDocumentation'),
      )
      .sort((a, b) => a.localeCompare(b));

    const headerLabelMap: Record<string, string> = {
      'report.reportName': 'Report Name',
      'report.practiceId': 'Practice ID',
      'report.chunkSize': 'Chunk Size',
      'report.totalRows': 'Total Rows',
      'report.processedRows': 'Processed Rows',
      'report.failedRows': 'Failed Rows',
      'report.skippedRows': 'Skipped Rows',
      'row.row': 'Row Number',
      'row.apptId': 'Appointment ID',
      'row.patientId': 'Patient ID',
      'row.encounterId': 'Encounter ID',
      'row.CPT_CODE': 'CPT Codes',
      'row.ICD_CODE': 'ICD10 Codes',
      'row.appointmenttype': 'Appointment Type',
      'row.appointment_time': 'Appointment Time',
      'row.encounter_procedures_json': 'Encounter Procedures',
      'row.encounter_procedure_codes': 'Encounter Procedure Codes',
      'row.hpi_notes_clean': 'HPI Notes',
      'row.physical_exam_notes_clean': 'Physical Exam Notes',
      'row.assessment_notes_clean': 'Assessment Notes',
      'row.procedure_notes_clean': 'Procedure Notes',
      'row.notes_clean': 'All Notes',
      'row.csvData.apptid': 'CSV Appointment ID',
      'row.csvData.apptdate': 'CSV Appointment Date',
      'row.csvData.patientid': 'CSV Patient ID',
      'row.csvData.claimid': 'CSV Claim ID',
    };

    const formatHeaderLabel = (key: string): string => {
      if (headerLabelMap[key]) {
        return headerLabelMap[key];
      }

      return key
        .replace(/^row\./, '')
        .replace(/^report\./, 'Report ')
        .replace(/\./g, ' ')
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const lines = [
      headers.map((h) => escapeCsvValue(formatHeaderLabel(h))).join(','),
      ...flattenedRows.map((row) =>
        headers.map((h) => escapeCsvValue(row[h] ?? '')).join(','),
      ),
    ];

    const csvContent = lines.join('\n');
    setCsvOutput(csvContent);
    downloadBlob(
      csvContent,
      `data_extraction_${practiceId || 'report'}_${formatTimestampForFilename(new Date())}.csv`,
    );
    showSuccess('CSV downloaded.');
  };

  return (
    <AppLayout currentPage="Data Extraction">
      <Navbar
        title="Data Extraction"
        showSearch={false}
        showAvatar={false}
        showWelcomeMessage={false}
      />

      <div className="p-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <Typography
            variant="p"
            color="neutral900"
            weight="semibold"
            size="18"
            className="mb-4"
          >
            Upload CSV and extract data
          </Typography>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <div className="md:col-span-1">
              <Typography
                variant="small"
                color="neutral700"
                size="12"
                className="mb-2 block"
              >
                Practice ID
              </Typography>
              <input
                value={practiceIdInput}
                onChange={(e) => setPracticeIdInput(e.target.value)}
                disabled={true}
                placeholder="Practice ID"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-base/30"
              />
              <Typography
                variant="small"
                color="neutral600"
                size="12"
                className="mt-1 block"
              >
                Prefilled from selected hospital:{' '}
                {selectedHospital?.hospitalName || '-'}
              </Typography>
            </div>

            <div className="md:col-span-1">
              <Typography
                variant="small"
                color="neutral700"
                size="12"
                className="mb-2 block"
              >
                CSV file
              </Typography>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="w-full h-10 rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="md:col-span-1 flex flex-col gap-3 md:items-end md:pt-7">
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Button
                  variant="primary"
                  onClick={handleExtract}
                  disabled={loading || isCancelling}
                  className="h-10"
                >
                  {loading ? 'Extracting...' : 'Extract'}
                </Button>
                {loading && jobId ? (
                  <Button
                    variant="outline"
                    onClick={handleCancelExtract}
                    disabled={isCancelling}
                    className="h-10"
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel'}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  onClick={handleDownloadCsv}
                  disabled={
                    loading ||
                    (!result?.data?.length &&
                      !(csvOutput || result?.csv || '').trim())
                  }
                  className="h-10"
                >
                  Download CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopyCsv}
                  disabled={loading || !(csvOutput || result?.csv || '').trim()}
                  className="h-10"
                >
                  Copy CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleTogglePreviousExtractions()}
                  disabled={loading || isCancelling}
                  className="h-10"
                >
                  {showPreviousExtractions
                    ? 'Hide Previous Extractions'
                    : 'Previous Extractions'}
                </Button>
              </div>
              <Checkbox
                label="Fetch with claim"
                checked={fetchWithClaim}
                onChange={(e) => setFetchWithClaim(e.target.checked)}
                disabled={loading || isCancelling}
              />
            </div>
          </div>

          {loading && jobId ? (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 space-y-2">
              <Typography variant="small" color="neutral900" size="12">
                Job ID: <span className="font-mono">{jobId}</span>
              </Typography>
              <Typography variant="small" color="neutral600" size="12">
                Status:{' '}
                <span className="font-medium text-neutral-800">
                  {jobProgress?.status ?? 'pending'}
                </span>
              </Typography>
              {typeof jobProgress?.progressPercentage === 'number' ? (
                <div className="pt-1">
                  <div className="h-2 w-full overflow-hidden rounded bg-gray-200">
                    <div
                      className="h-2 rounded bg-neutral-900 transition-all"
                      style={{
                        width: `${Math.min(100, Math.max(0, jobProgress.progressPercentage))}%`,
                      }}
                    />
                  </div>
                  <Typography
                    variant="small"
                    color="neutral500"
                    size="12"
                    className="mt-1"
                  >
                    {Math.round(jobProgress.progressPercentage)}% complete
                  </Typography>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
                {jobProgress?.csvProcessing ? (
                  <>
                    <span>
                      CSV rows: {jobProgress.csvProcessing.processed ?? 0} /{' '}
                      {jobProgress.csvProcessing.totalRows ?? '?'}
                    </span>
                    <span>
                      Failed: {jobProgress.csvProcessing.failed ?? 0} · Skipped:{' '}
                      {jobProgress.csvProcessing.skipped ?? 0}
                    </span>
                  </>
                ) : null}
                {(jobProgress?.processedRows != null ||
                  jobProgress?.failedRows != null ||
                  jobProgress?.skippedRows != null) && (
                  <span>
                    Totals — processed: {jobProgress?.processedRows ?? '—'},
                    failed: {jobProgress?.failedRows ?? '—'}, skipped:{' '}
                    {jobProgress?.skippedRows ?? '—'}
                  </span>
                )}
              </div>
            </div>
          ) : null}

          {extractError && (
            <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
              <Typography variant="small" color="destructive" size="12">
                {extractError}
              </Typography>
            </div>
          )}

          {showPreviousExtractions ? (
            <div className="mt-4 rounded-md border border-gray-200 p-4">
              <Typography
                variant="small"
                color="neutral900"
                size="12"
                className="mb-3 block"
              >
                Previous Extraction Requests (latest 10)
              </Typography>
              {previousExtractionsLoading ? (
                <Typography variant="small" color="neutral600" size="12">
                  Loading previous extractions...
                </Typography>
              ) : previousExtractionsError ? (
                <Typography variant="small" color="destructive" size="12">
                  {previousExtractionsError}
                </Typography>
              ) : (
                <div className="border border-gray-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">Request ID</th>
                        <th className="text-left p-3">Job ID</th>
                        <th className="text-left p-3">Practice ID</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Rows</th>
                        <th className="text-left p-3">Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previousExtractions.map((item) => (
                        <tr key={item.uuid} className="border-t">
                          <td className="p-3 font-mono text-xs">{item.uuid}</td>
                          <td className="p-3 font-mono text-xs">
                            {item.jobId || '-'}
                          </td>
                          <td className="p-3">{item.practiceId || '-'}</td>
                          <td className="p-3">{item.status || '-'}</td>
                          <td className="p-3">
                            {item.processedRows ?? 0} / {item.totalRows ?? 0}
                          </td>
                          <td className="p-3">
                            {item.createdAt
                              ? new Date(item.createdAt).toLocaleString()
                              : '-'}
                          </td>
                        </tr>
                      ))}
                      {previousExtractions.length === 0 ? (
                        <tr className="border-t">
                          <td className="p-3 text-neutral-500" colSpan={6}>
                            No previous extraction requests found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          {result && (
            <div className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography
                    variant="small"
                    color="neutral700"
                    size="12"
                    className="mb-2 block"
                  >
                    JSON Response
                  </Typography>
                  <pre className="max-h-64 overflow-auto rounded bg-gray-50 p-3 text-xs whitespace-pre-wrap wrap-break-word">
                    {JSON.stringify(result, null, 2)}
                  </pre>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography
                    variant="small"
                    color="neutral700"
                    size="12"
                    className="mb-2 block"
                  >
                    CSV Output
                  </Typography>
                  <textarea
                    readOnly
                    value={csvOutput || result.csv || ''}
                    className="h-64 w-full rounded border border-gray-200 bg-gray-50 p-3 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography variant="small" color="neutral600" size="12">
                    Total rows
                  </Typography>
                  <Typography variant="p" color="neutral900" weight="semibold">
                    {result.totalRows ??
                      jobProgress?.csvProcessing?.totalRows ??
                      0}
                  </Typography>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography variant="small" color="neutral600" size="12">
                    Processed
                  </Typography>
                  <Typography variant="p" color="neutral900" weight="semibold">
                    {result.processedRows ?? jobProgress?.processedRows ?? 0}
                  </Typography>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography variant="small" color="neutral600" size="12">
                    Failed
                  </Typography>
                  <Typography variant="p" color="neutral900" weight="semibold">
                    {result.failedRows ?? jobProgress?.failedRows ?? 0}
                  </Typography>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography variant="small" color="neutral600" size="12">
                    Skipped
                  </Typography>
                  <Typography variant="p" color="neutral900" weight="semibold">
                    {result.skippedRows ?? jobProgress?.skippedRows ?? 0}
                  </Typography>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <Typography variant="small" color="neutral600" size="12">
                    Chunk size
                  </Typography>
                  <Typography variant="p" color="neutral900" weight="semibold">
                    {result.chunkSize ?? 0}
                  </Typography>
                </div>
              </div>

              {/* Structured rows from result.data (Athena-style) */}
              {previewRows.length > 0 && (
                <div className="border border-gray-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3">Row</th>
                        <th className="text-left p-3">Appt ID</th>
                        {hasClaimIdInResults ? (
                          <th className="text-left p-3">Claim ID</th>
                        ) : null}
                        <th className="text-left p-3">Patient ID</th>
                        <th className="text-left p-3">Encounter ID</th>
                        <th className="text-left p-3">CPT CODE</th>
                        <th className="text-left p-3">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((item, index) => (
                        <tr
                          key={`${item.row || index}`}
                          className="border-t h-28"
                        >
                          <td className="p-3">{item.row || '-'}</td>
                          <td className="p-3">
                            {item.apptId || item.csvData?.apptid || '-'}
                          </td>
                          {hasClaimIdInResults ? (
                            <td className="p-3">
                              {item.csvData?.claimid || '-'}
                            </td>
                          ) : null}
                          <td className="p-3">
                            {item.patientId || item.csvData?.patientid || '-'}
                          </td>
                          <td className="p-3">{item.encounterId || '-'}</td>
                          <td className="p-3">{item.CPT_CODE || '-'}</td>
                          <td className="p-3 align-top">
                            <div className="max-w-xs h-20 overflow-x-auto overflow-y-hidden whitespace-nowrap text-xs text-neutral-700">
                              {String(item._notes || '-')
                                .replace(/\s+/g, ' ')
                                .trim()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Flat CSV rows (ECW-style: no result.data, only result.csv) */}
              {previewRows.length === 0 && parsedCsvRows.length > 0 && (
                <div className="border border-gray-200 rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {parsedCsvHeaders.map((h) => (
                          <th
                            key={h}
                            className="text-left p-3 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedCsvRows.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-t">
                          {parsedCsvHeaders.map((h) => (
                            <td key={h} className="p-3 align-top max-w-xs">
                              <div className="max-h-20 overflow-y-auto text-xs text-neutral-700 whitespace-pre-wrap wrap-break-word">
                                {row[h] || '-'}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {previewRows.length === 0 && parsedCsvRows.length === 0 && (
                <div className="border border-gray-200 rounded-md p-4">
                  <Typography variant="small" color="neutral500" size="12">
                    No rows returned.
                  </Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default DataExtraction;
